import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import '@/app.css'
import * as React from 'react'
import * as Sentry from '@sentry/react'
import {
HeadContent,
Outlet,
Scripts,
createRootRouteWithContext,
useRouterState,
} from '@tanstack/react-router'
import { QueryClient,QueryClientProvider } from '@tanstack/react-query'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '@/router'
import { Toaster } from '@/components/ui/sonner'
import { ThemeProvider } from '@/components/theme-provider'
import { AxiosGlobalConfig } from '@/components/axios-global-config'
import { SessionExpiredDialog } from '@/components/session-expired-dialog'
import { PullToRefresh } from '@/components/pull-to-refresh'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'RootPoint' },
    ],
    links: [
      { rel: 'icon', href: '/favicons/favicon.ico' },
    ],
  }),
  component: RootComponent,
  errorComponent: RouteError,
  pendingComponent: RoutePending,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isTerminal = pathname === '/terminal'

  return (
    <RootDocument>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
          <AxiosGlobalConfig />
          <SessionExpiredDialog />
          <PullToRefresh />
          <Sentry.ErrorBoundary fallback={<p>An unexpected error occurred.</p>}>
            <Outlet />
          </Sentry.ErrorBoundary>
          <Toaster position={isTerminal ? 'bottom-center' : undefined} />
          {import.meta.env.DEV && (
            <>
              <TanStackRouterDevtools position="bottom-right" />
              <ReactQueryDevtools initialIsOpen={false} />
            </>
          )}
        </ThemeProvider>
      </QueryClientProvider>
    </RootDocument>
  )
}
